import React, { useState } from "react";
import { IVehicle } from "../models/Vehicle";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { VehicleService } from "../services/VehicleService";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { Toast } from "../../../Shared/components/Toast";
import { AuthService } from "../../Auth/services/AuthService";
import { GenericZoomFile } from "../../../Shared/components/GenericZoomFile";
import { DriverForm } from "./DriverForm";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { useTranslation } from "react-i18next";

interface VehicleDetailProps {
  vehicle?: IVehicle;
}

const XDiv = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) => (
  <div>
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    <p className="font-medium text-gray-900 dark:text-white">{value || "—"}</p>
  </div>
);

export const Vehicle: React.FC<VehicleDetailProps> = () => {
  const {t} = useTranslation()
  const message = t('loading_message');

  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicle");

  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const findVehicle = async () => {
    try {
      setIsLoading(true);
      return await VehicleService.getById(vehicleId ?? "");
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: ["vehicle"],
    queryFn: findVehicle,
    placeholderData: (previous) => previous?.data,
  });

  let isAllowed = AuthService.isManagementAllowed();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };
  const onAddDriverAction = () => setIsModalOpen(!isModalOpen);

  return (
    <>
      {isModalOpen && (
        <DriverForm onClose={toggleModal} vehicle={query.data!} />
      )}
      <div className="px-5 mx-5 pb-10 mb-10">
        <div className="px-6 bg-white dark:bg-gray-800 rounded-xl shadow-xxl mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">
              {query.data?.name}
            </h2>

            <div className="flex">
              {!isAllowed && (
                <GenericButton
                  isFull={false}
                  type="button"
                  onClick={onAddDriverAction}
                >
                  {t('create_driver')}
                </GenericButton>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <XDiv label={t('truck_make_label')} value={query.data?.name} />
            <XDiv label={t('truck_year_label')} value={query.data?.truckNumber} />
            <XDiv label={t('truck_chassis_label')} value={query.data?.chassisNumber} />
            <XDiv label={t('truck_registration_label')} value={query.data?.plateNumber} />
            <XDiv label={t('truck_type_label')} value={query.data?.vehicleType} />
            <XDiv label={t('truck_min_weight_label')} value={query.data?.minimumWeight} />
            <XDiv label={t('truck_max_weight_label')} value={query.data?.maximumWeight} />

            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {t('truck_description_label')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {query.data?.description || "—"}
              </p>
            </div>

            <XDiv label={t('company_name_label')} value={query.data?.company?.name} />
            <XDiv
              label={t('company_address_label')}
              value={query.data?.company?.address}
            />

            <div className="">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                {t('menu_drivers')}
              </label>
              {query.data?.drivers?.length ? (
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-400">
                  {query.data.drivers.map((driver: any, index: any) => (
                    <li key={index}>
                      {driver.firstName} {driver.lastName}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                 {t('no_drivers_text')}
                </p>
              )}
            </div>

            <div className="col-span-1 md:col-span-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                {t('attachments')}
              </label>
              <div className="list-disc list-inside flex flex-row gap-4 text-gray-600 dark:text-gray-400">
                {query.data?.attachments?.map((attach: any, index: any) => (
                  <div key={index}>
                    <GenericZoomFile src={attach} height={100} width={100} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          {showToast && (
            <Toast
              type={toast?.type}
              message={toast?.message ?? ""}
              onClose={() => setShowToast(false)}
            />
          )}
          {isLoading && <OverlayLoader message={message} />}
        </div>
      </div>
    </>
  );
};
